import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import Heading from '../Heading';
import { cn } from '@/lib/utils';

const courseSections = {
  entrepreneurs: [
    {
      id: 1,
      title: 'Tech & AI Foundations for All Professionals',
      subtitle:
        'Build essential technical literacy and AI fundamentals for mastering AI',
      status: 'expanded',
      image: '/images/GenAI/ContentOne.png',
      content: (
        <>
          <p>
            Develop the technical foundation every modern professional needs.
            Learn core technology concepts like frontend vs backend, APIs,
            databases, and cloud computing, then master AI fundamentals
            including how different models work, their capabilities, and
            practical applications in professional workflows.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              Essential tech concepts: frontend/backend, APIs, databases, cloud
              services, mobile vs web
            </li>
            <li>
              Software development lifecycle and how teams collaborate with
              engineering
            </li>
            <li>
              AI fundamentals: LLMs, machine learning, automation, and when to
              use each type
            </li>
            <li>
              Prompt engineering techniques for consistent, professional AI
              outputs
            </li>
            <li>
              Technical communication skills to bridge different teams and
              stakeholders
            </li>
            <li>
              AI tool evaluation framework and implementation best practices
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> ChatGPT,
            Claude, Gemini, Perplexity, Zapier AI
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: 'AI-Powered Business Validation & Strategy',
      subtitle:
        'Use AI to validate ideas, understand markets, and build data-driven business models',
      status: 'expanded',
      image: '/images/GenAI/ContentTwo.png',
      content: (
        <>
          <p>
            Deploy AI agents to conduct market research, analyze competition,
            and validate business assumptions at unprecedented speed. Build
            intelligent systems that continuously gather market intelligence and
            guide strategic decisions without expensive consultants or large
            research teams.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              AI-driven business model design and market opportunity assessment
            </li>
            <li>
              Automated competitive intelligence and trend analysis systems
            </li>
            <li>AI-powered financial modeling and scenario planning</li>
            <li>Building intelligent market validation workflows</li>
            <li>Creating self-updating business intelligence dashboards</li>
            <li>
              Scaling decision-making with AI-assisted strategic frameworks
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span>{' '}
            Perplexity, Zapier, Airtable AI, business intelligence platforms,
            automated research tools
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Build an
            AI-powered business validation system that automatically researches
            markets, analyzes competition, and generates strategic
            recommendations for your venture
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: 'AI-Enhanced Customer Intelligence & Product-Market Fit',
      subtitle:
        'Deploy AI agents to understand customers and achieve product-market fit faster',
      status: 'expanded',
      image: '/images/GenAI/ContentThree.png',
      content: (
        <>
          <p>
            Create intelligent customer research systems that automatically
            analyze conversations, extract insights, and guide product
            decisions. Use AI to process hundreds of customer interactions and
            identify patterns that lead to product-market fit.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              AI-powered customer interview analysis and insight synthesis
            </li>
            <li>Automated feedback processing and pattern recognition</li>
            <li>
              Building intelligent customer feedback loops for continuous
              learning
            </li>
            <li>AI-driven persona creation and journey mapping</li>
            <li>Predictive customer behavior modeling and segmentation</li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Otter.ai,
            Typeform AI, customer analytics platforms, sentiment analysis tools
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Create an
            AI customer intelligence system that automatically processes
            feedback, identifies opportunities, and predicts customer needs
          </p>
        </>
      ),
    },
    {
      id: 4,
      title: 'AI-Driven Growth & Operations Automation',
      subtitle: '',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
    {
      id: 5,
      title: 'AI-Enhanced Fundraising & Investor Relations',
      subtitle: '',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
  ],
  'product-marketing-management': [
    {
      id: 1,
      title: 'Tech & AI Foundations for All Professionals',
      subtitle:
        'Build essential technical literacy and AI fundamentals for mastering AI',
      status: 'expanded',
      image: '/images/GenAI/ContentOne.png',
      content: (
        <>
          <p>
            Develop the technical foundation every modern professional needs.
            Learn core technology concepts like frontend vs backend, APIs,
            databases, and cloud computing, then master AI fundamentals
            including how different models work, their capabilities, and
            practical applications in professional workflows.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              Essential tech concepts: frontend/backend, APIs, databases, cloud
              services, mobile vs web
            </li>
            <li>
              Software development lifecycle and how teams collaborate with
              engineering
            </li>
            <li>
              AI fundamentals: LLMs, machine learning, automation, and when to
              use each type
            </li>
            <li>
              Prompt engineering techniques for consistent, professional AI
              outputs
            </li>
            <li>
              Technical communication skills to bridge different teams and
              stakeholders
            </li>
            <li>
              AI tool evaluation framework and implementation best practices
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> ChatGPT,
            Claude, Gemini, Perplexity, Zapier AI
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: 'AI-Enhanced Product Research & Market Intelligence',
      subtitle:
        'Deploy AI agents to gather comprehensive user and market insights at unprecedented scale',
      status: 'expanded',
      image: '/images/GenAI/ContentTwo.png',
      content: (
        <>
          <p>
            Transform raw data into actionable product insights using AI
            research and analysis tools. Combine traditional research
            methodologies with AI-powered analysis to process thousands of data
            points, identify patterns, and generate insights that would
            traditionally take weeks to uncover.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              Automated user feedback analysis and sentiment detection at scale
            </li>
            <li>
              AI-driven competitive intelligence and market trend identification
            </li>
            <li>Data-driven persona creation and user journey mapping</li>
            <li>
              Advanced prompt techniques for research synthesis and insight
              generation
            </li>
            <li>
              Building research systems that continuously learn and improve
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span>{' '}
            MonkeyLearn, Otter.ai, Dovetail AI, Perplexity, Similarweb AI,
            Crayon, Brandwatch AI
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Build an
            AI-powered research intelligence system that automatically analyzes
            user feedback, tracks competitive moves, and generates actionable
            market insights for product decision-making
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: 'AI-Powered Prototyping & Product Validation',
      subtitle:
        'Build functional prototypes and validate product concepts using AI automation',
      status: 'expanded',
      image: '/images/GenAI/ContentThree.png',
      content: (
        <>
          <p>
            Create and test product concepts rapidly using AI-powered
            prototyping tools that require minimal technical resources. Deploy
            intelligent validation systems that gather user feedback and
            optimize product features based on real user behavior.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>No-code AI product development and rapid prototyping</li>
            <li>Automated user testing and feedback collection systems</li>
            <li>AI-driven feature prioritization and roadmap optimization</li>
            <li>Building intelligent A/B testing frameworks</li>
            <li>Creating self-optimizing product experiences</li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Bubble AI,
            Webflow AI, Figma AI, Maze AI, product analytics platforms
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Create a
            fully functional product prototype with AI-powered user testing and
            optimization systems
          </p>
        </>
      ),
    },
    {
      id: 4,
      title: 'Strategic Product Planning with AI Intelligence',
      subtitle:
        'Create data-driven roadmaps, requirements, and strategic frameworks using AI',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
    {
      id: 5,
      title: 'Go-to-Market Mastery - AI-Enhanced Launch & Communication',
      subtitle:
        'Master messaging, stakeholder alignment, and launch execution with AI tools',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
  ],
  marketing: [
    {
      id: 1,
      title: 'Tech & AI Foundations for All Professionals',
      subtitle:
        'Build essential technical literacy and AI fundamentals for mastering AI',
      status: 'expanded',
      image: '/images/GenAI/ContentOne.png',
      content: (
        <>
          <p>
            Develop the technical foundation every modern professional needs.
            Learn core technology concepts like frontend vs backend, APIs,
            databases, and cloud computing, then master AI fundamentals
            including how different models work, their capabilities, and
            practical applications in professional workflows.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              Essential tech concepts: frontend/backend, APIs, databases, cloud
              services, mobile vs web
            </li>
            <li>
              Software development lifecycle and how teams collaborate with
              engineering
            </li>
            <li>
              AI fundamentals: LLMs, machine learning, automation, and when to
              use each type
            </li>
            <li>
              Prompt engineering techniques for consistent, professional AI
              outputs
            </li>
            <li>
              Technical communication skills to bridge different teams and
              stakeholders
            </li>
            <li>
              AI tool evaluation framework and implementation best practices
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> ChatGPT,
            Claude, Gemini, Perplexity, Zapier AI
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: 'AI Content Creation & Brand Intelligence',
      subtitle:
        'Build AI-powered content engines that create compelling, on-brand content at scale',
      status: 'expanded',
      image: '/images/GenAI/ContentTwo.png',
      content: (
        <>
          <p>
            Master AI content creation across all formats while maintaining
            authenticity and engagement. Deploy intelligent content systems that
            understand your brand voice, audience preferences, and performance
            patterns to create content that converts.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              AI-powered copywriting systems for different channels and
              audiences
            </li>
            <li>
              Visual content creation using AI design and video generation tools
            </li>
            <li>Building AI brand voice models for consistent messaging</li>
            <li>
              Automated content strategy development and editorial calendar
              planning
            </li>
            <li>Performance-driven content optimization using AI analytics</li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Jasper,
            Copy.ai, Canva AI, Pictory, RunwayML, brand voice AI platforms
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Build an
            AI-powered content creation system that generates consistent,
            on-brand content across multiple channels with automated performance
            optimization
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: 'AI Audience Intelligence & Campaign Optimization',
      subtitle:
        'Deploy AI systems to understand audiences deeply and optimize campaign performance automatically',
      status: 'expanded',
      image: '/images/GenAI/ContentThree.png',
      content: (
        <>
          <p>
            Use AI to analyze customer behavior, predict preferences, and
            optimize campaigns in real-time. Build intelligent marketing systems
            that continuously learn from performance data and automatically
            adjust targeting, messaging, and spend allocation.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>AI-powered audience segmentation and behavioral analysis</li>
            <li>Predictive customer lifetime value and churn modeling</li>
            <li>Automated campaign optimization and budget allocation</li>
            <li>
              Building intelligent attribution and performance tracking systems
            </li>
            <li>Creating self-optimizing marketing funnels</li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Facebook
            Ads AI, Google Ads AI, customer data platforms, predictive analytics
            tools
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Create an
            AI-powered marketing optimization system that automatically segments
            audiences, personalizes messaging, and optimizes campaign
            performance
          </p>
        </>
      ),
    },
    {
      id: 4,
      title: 'Digital Advertising & Conversion AI',
      subtitle:
        'Master AI-powered advertising platforms and automated conversion optimization',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
    {
      id: 5,
      title: 'Marketing Intelligence & Predictive Analytics',
      subtitle:
        'Transform marketing data into predictive insights using advanced AI analytics',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
  ],
  consulting: [
    {
      id: 1,
      title: 'Tech & AI Foundations for All Professionals',
      subtitle:
        'Build essential technical literacy and AI fundamentals for mastering AI',
      status: 'expanded',
      image: '/images/GenAI/ContentOne.png',
      content: (
        <>
          <p>
            Develop the technical foundation every modern professional needs.
            Learn core technology concepts like frontend vs backend, APIs,
            databases, and cloud computing, then master AI fundamentals
            including how different models work, their capabilities, and
            practical applications in professional workflows.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              Essential tech concepts: frontend/backend, APIs, databases, cloud
              services, mobile vs web
            </li>
            <li>
              Software development lifecycle and how teams collaborate with
              engineering
            </li>
            <li>
              AI fundamentals: LLMs, machine learning, automation, and when to
              use each type
            </li>
            <li>
              Prompt engineering techniques for consistent, professional AI
              outputs
            </li>
            <li>
              Technical communication skills to bridge different teams and
              stakeholders
            </li>
            <li>
              AI tool evaluation framework and implementation best practices
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> ChatGPT,
            Claude, Gemini, Perplexity, Zapier AI
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: 'AI-Powered Research & Intelligence Systems',
      subtitle:
        'Deploy AI agents to conduct comprehensive research and analysis at unprecedented speed',
      status: 'expanded',
      image: '/images/GenAI/ContentTwo.png',
      content: (
        <>
          <p>
            Transform how you gather and analyze information for clients using
            AI tools that process vast amounts of data quickly. Build
            intelligent research systems that continuously monitor industries,
            competitors, and trends to provide ongoing strategic insights.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>AI-powered industry and market research automation</li>
            <li>
              Competitive landscape analysis using intelligent monitoring
              systems
            </li>
            <li>
              Building AI-driven data synthesis and insight generation
              frameworks
            </li>
            <li>
              Creating automated research workflows that update continuously
            </li>
            <li>
              Developing compelling visualizations and presentations from AI
              analysis
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span>{' '}
            Perplexity, Crayon, Similarweb, research automation tools,
            AI-powered analytics platforms
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Build an
            AI-powered research intelligence system that automatically gathers,
            analyzes, and synthesizes market intelligence for ongoing client
            advisory work
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: 'AI-Enhanced Strategic Framework Development',
      subtitle:
        'Create powerful strategic frameworks and recommendations using AI-powered analysis',
      status: 'expanded',
      image: '/images/GenAI/ContentThree.png',
      content: (
        <>
          <p>
            Use AI to develop custom strategic frameworks, analyze complex
            business problems, and generate data-driven recommendations. Build
            intelligent consulting methodologies that enhance traditional
            frameworks with AI-powered insights and predictions.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              AI-assisted strategic framework development and customization
            </li>
            <li>
              Building intelligent problem-solving and recommendation engines
            </li>
            <li>Using AI for scenario planning and strategic modeling</li>
            <li>
              Creating data-driven strategy presentations and deliverables
            </li>
            <li>
              Developing AI-powered benchmarking and performance analysis tools
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Strategic
            planning AI tools, business modeling platforms, presentation AI
            assistants
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Create an
            AI-powered strategic consulting toolkit that generates custom
            frameworks, analyzes business scenarios, and produces strategic
            recommendations
          </p>
        </>
      ),
    },
    {
      id: 4,
      title: 'Client Delivery & AI-Enhanced Communication',
      subtitle:
        'Enhance client presentations and deliverables with AI-powered communication tools',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
    {
      id: 5,
      title: 'Practice Scaling & AI Knowledge Management',
      subtitle:
        'Build scalable consulting systems and capture institutional knowledge using AI',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
  ],
  'design-ui-ux': [
    {
      id: 1,
      title: 'Tech & AI Foundations for All Professionals',
      subtitle:
        'Build essential technical literacy and AI fundamentals for mastering AI',
      status: 'expanded',
      image: '/images/GenAI/ContentOne.png',
      content: (
        <>
          <p>
            Develop the technical foundation every modern professional needs.
            Learn core technology concepts like frontend vs backend, APIs,
            databases, and cloud computing, then master AI fundamentals
            including how different models work, their capabilities, and
            practical applications in professional workflows.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              Essential tech concepts: frontend/backend, APIs, databases, cloud
              services, mobile vs web
            </li>
            <li>
              Software development lifecycle and how teams collaborate with
              engineering
            </li>
            <li>
              AI fundamentals: LLMs, machine learning, automation, and when to
              use each type
            </li>
            <li>
              Prompt engineering techniques for consistent, professional AI
              outputs
            </li>
            <li>
              Technical communication skills to bridge different teams and
              stakeholders
            </li>
            <li>
              AI tool evaluation framework and implementation best practices
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> ChatGPT,
            Claude, Gemini, Perplexity, Zapier AI
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: 'AI-Enhanced User Research & Behavioral Analysis',
      subtitle:
        'Deploy AI systems to understand users deeply and extract actionable design insights',
      status: 'expanded',
      image: '/images/GenAI/ContentTwo.png',
      content: (
        <>
          <p>
            Transform user research using AI tools that analyze behavior,
            process feedback, and identify patterns in user data. Build
            intelligent research systems that continuously gather user insights
            and inform better design decisions automatically.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>AI-powered user interview analysis and insight extraction</li>
            <li>Automated behavioral data analysis and pattern recognition</li>
            <li>
              Building intelligent usability testing and accessibility auditing
              systems
            </li>
            <li>
              Creating data-driven user personas and journey maps using AI
            </li>
            <li>
              Developing AI-enhanced A/B testing frameworks for design
              validation
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Otter.ai,
            Hotjar AI, UserTesting AI, Maze AI, user research automation
            platforms
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Build an
            AI-powered user research system that automatically analyzes user
            feedback, identifies usability issues, and generates design
            recommendations
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: 'AI-Powered Design Creation & Rapid Prototyping',
      subtitle:
        'Create functional prototypes and design systems using AI-enhanced creative tools',
      status: 'expanded',
      image: '/images/GenAI/ContentThree.png',
      content: (
        <>
          <p>
            Master AI-powered design creation while maintaining human creativity
            and design principles. Build intelligent design systems that
            generate variations, create prototypes, and maintain consistency
            across all design work.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              AI-powered ideation and concept generation for design projects
            </li>
            <li>
              Creating design systems and component libraries with AI assistance
            </li>
            <li>
              Building rapid prototyping workflows using AI-enhanced tools
            </li>
            <li>Automated design variation generation and optimization</li>
            <li>
              Maintaining design quality and brand consistency with AI tools
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Figma AI,
            Adobe AI tools, Midjourney, design system generators, prototyping AI
            platforms
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Create a
            complete design system for a digital product using AI tools for
            ideation, asset generation, and automated documentation
          </p>
        </>
      ),
    },
    {
      id: 4,
      title: 'Design Systems & AI-Enhanced Collaboration',
      subtitle:
        'Build scalable design systems and enhance team collaboration with intelligent automation',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
    {
      id: 5,
      title: 'Performance Optimization & AI-Powered Testing',
      subtitle:
        'Optimize designs for performance and validate decisions with AI-powered testing frameworks',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
  ],
  operations: [
    {
      id: 1,
      title: 'Tech & AI Foundations for All Professionals',
      subtitle:
        'Build essential technical literacy and AI fundamentals for mastering AI',
      status: 'expanded',
      image: '/images/GenAI/ContentOne.png',
      content: (
        <>
          <p>
            Develop the technical foundation every modern professional needs.
            Learn core technology concepts like frontend vs backend, APIs,
            databases, and cloud computing, then master AI fundamentals
            including how different models work, their capabilities, and
            practical applications in professional workflows.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              Essential tech concepts: frontend/backend, APIs, databases, cloud
              services, mobile vs web
            </li>
            <li>
              Software development lifecycle and how teams collaborate with
              engineering
            </li>
            <li>
              AI fundamentals: LLMs, machine learning, automation, and when to
              use each type
            </li>
            <li>
              Prompt engineering techniques for consistent, professional AI
              outputs
            </li>
            <li>
              Technical communication skills to bridge different teams and
              stakeholders
            </li>
            <li>
              AI tool evaluation framework and implementation best practices
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> ChatGPT,
            Claude, Gemini, Perplexity, Zapier AI
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: 'AI-Powered Process Automation & Optimization',
      subtitle:
        'Deploy intelligent automation systems to optimize workflows and eliminate inefficiencies',
      status: 'expanded',
      image: '/images/GenAI/ContentTwo.png',
      content: (
        <>
          <p>
            Transform business operations using AI tools that automate routine
            tasks, predict bottlenecks, and optimize workflows. Build
            intelligent process management systems that continuously improve
            operational efficiency and resource allocation.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>AI-powered workflow automation and process optimization</li>
            <li>
              Building intelligent resource planning and allocation systems
            </li>
            <li>Creating predictive analytics for operational forecasting</li>
            <li>
              Developing automated performance monitoring and KPI tracking
            </li>
            <li>
              Implementing AI-driven quality management and continuous
              improvement
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Zapier,
            Airtable AI, Monday.com AI, process automation platforms, predictive
            analytics tools
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Design and
            implement an AI-powered operations management system that automates
            key processes and provides real-time performance optimization
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: 'Data-Driven Decision Making & Predictive Analytics',
      subtitle:
        'Transform operational data into predictive insights using AI-powered analytics systems',
      status: 'expanded',
      image: '/images/GenAI/ContentThree.png',
      content: (
        <>
          <p>
            Build intelligent analytics systems that collect, analyze, and act
            on operational data automatically. Deploy AI tools that identify
            patterns, predict issues, and recommend optimizations for continuous
            operational improvement.
          </p>

          <p className="mt-4 font-semibold">What You'll Learn:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>
              Operational data collection and AI-powered analysis strategies
            </li>
            <li>
              Building predictive analytics for demand forecasting and resource
              planning
            </li>
            <li>
              Creating intelligent performance benchmarking and optimization
              systems
            </li>
            <li>
              Developing automated dashboards and reporting with AI insights
            </li>
            <li>
              Implementing AI-assisted root cause analysis and problem-solving
              frameworks
            </li>
          </ul>

          <p className="mt-4">
            <span className="font-semibold">Tools & Platforms:</span> Tableau
            AI, Power BI, operational analytics platforms, predictive modeling
            tools
          </p>

          <p className="mt-2">
            <span className="font-semibold">Capstone Project:</span> Build a
            comprehensive operational intelligence system that monitors
            performance, predicts issues, and automatically generates
            improvement strategies
          </p>
        </>
      ),
    },
    {
      id: 4,
      title: 'Supply Chain & AI-Enhanced Resource Optimization',
      subtitle:
        'Optimize supply chains and resource allocation using AI-powered planning and forecasting tools',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
    {
      id: 5,
      title: 'Strategic Operations & AI-Powered Scaling',
      subtitle:
        'Scale operations efficiently and plan strategically using AI insights and automation',
      status: 'locked',
      image: '/images/GenAI/ContentFour.png',
    },
  ],
};

const CourseAccordion = ({ slug }) => {
  const [openId, setOpenId] = useState(null);

  const data = courseSections?.[slug];

  const toggleAccordion = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="my-6">
      <Heading>Course Contents</Heading>

      {data.map((section) => (
        <div
          key={section.id}
          className={cn(
            'mb-4 rounded-lg p-4 shadow-md',
            section.status === 'locked'
              ? 'bg-gray-100 text-gray-400'
              : 'bg-teal-50 text-gray-800',
          )}
        >
          <div
            className={cn(
              'flex items-center justify-between gap-4',
              section.status === 'locked' ? '' : 'cursor-pointer',
            )}
            onClick={() =>
              section.status === 'expanded' && toggleAccordion(section.id)
            }
          >
            <div className="flex items-center gap-4">
              <div className="flex h-[50px] w-[50px] items-center justify-center rounded-full bg-[#CDDFDC]">
                {section.status === 'locked' ? (
                  <FontAwesomeIcon icon={faLock} className={'text-gray-400'} />
                ) : (
                  <img
                    src={section.image}
                    className="w-full"
                    alt={section.image}
                  />
                )}
              </div>
              <div className="flex-1">
                <h3
                  className={cn(
                    'font-manrope text-[12px] font-extrabold capitalize leading-[1.5] text-gray-900',
                    'md:text-[18px] md:leading-[1.5]',
                  )}
                >
                  {section.title}
                </h3>
                {section.subtitle && (
                  <p
                    className={cn(
                      'text-[12px] font-medium capitalize leading-[12.13px] text-[#7A8B87]',
                      'md:text-[16px] md:leading-[26px]',
                    )}
                  >
                    {section.subtitle}
                  </p>
                )}
              </div>
            </div>
            {section.status === 'expanded' && (
              <div className="mt-1 text-[#7A8B87]">
                <FontAwesomeIcon
                  size="lg"
                  icon={openId === section.id ? 'chevron-up' : 'chevron-down'}
                />
              </div>
            )}
          </div>

          {section.status === 'expanded' && openId === section.id && (
            <div
              className={cn(
                'ml-4 mt-4 text-[12px] font-medium capitalize leading-[19px] text-[#7A8B87]',
                'md:text-[16px] md:leading-[26px]',
              )}
            >
              {section.content}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CourseAccordion;
